import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { aparecer, fila, revelarDesde, resorte, suave } from '../components/movimiento';
import { api, textoUbicacion } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useSesion } from '../lib/sesion';
import type { Vista } from '../components/Layout';
import { Cargando, Encabezado, Numero, PanelError, useConfirmar, Vacio } from '../components/ui';
import { aviso, botonPrimario, c, campo, encabezadoTabla, enlace, etiquetaCampo, filaTabla, miles, mono, tarjeta } from '../theme';

/**
 * Vaciar un rack: se elige el rack, se marcan los pallets (o todos) y vuelven
 * a la cola de sin ubicar. Ni el rack ni los pallets se borran; es sacarlos de
 * las posiciones de una vez, cuando un rack se cargó mal o se desarma.
 */
export default function VaciarRack({
  rackInicial,
  onIr,
}: {
  /** El rack desde el que se llegó (el panel del mapa): entra ya elegido. */
  rackInicial?: string | null;
  onIr: (v: Vista, id?: string) => void;
}) {
  const { vistas } = useSesion();
  const { datos, cargando, error, recargar } = useApi(() => api.mapa(''), [], VIVO.normal);
  const [rackId, setRackId] = useState(rackInicial ?? '');
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [trabajando, setTrabajando] = useState(false);
  const [hecho, setHecho] = useState('');
  /** Los pallets que acaban de quedar sin ubicar: de acá se llega a su ficha. */
  const [liberados, setLiberados] = useState<string[]>([]);
  const [fallo, setFallo] = useState('');
  const [confirmar, dialogo] = useConfirmar();

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando />;
  if (!datos) return null;

  const racks = datos.sectores.flatMap((s) => s.racks.map((r) => ({ ...r, sector: s.nombre })));
  const rack = racks.find((r) => r.id === rackId);
  // Lo que hay arriba del rack: un renglón por pallet, sea de nivel o de zona de piso.
  const ocupados = (rack?.posiciones ?? []).flatMap((p) =>
    [...p.niveles.filter((n) => n.pallet), ...(p.zona ?? []).map((z) => ({ ...z, nivel: null }))].map((n) => ({
      pallet: n.pallet!,
      articulo: n.articulo,
      piezas: n.tipo === 'completo' ? null : n.piezas,
      ubicacion: textoUbicacion(p.codigo, n.nivel),
    })),
  );
  const todos = ocupados.length > 0 && ocupados.every((o) => marcados.has(o.pallet));

  function marcar(pallet: string) {
    const otro = new Set(marcados);
    if (otro.has(pallet)) otro.delete(pallet);
    else otro.add(pallet);
    setMarcados(otro);
  }

  async function vaciar() {
    const elegidos = ocupados.filter((o) => marcados.has(o.pallet)).map((o) => o.pallet);
    const ok = await confirmar({
      titulo: `¿Vaciar ${elegidos.length} ${elegidos.length === 1 ? 'posición' : 'posiciones'} de ${rack!.id}?`,
      detalle: 'Los pallets quedan sin ubicar y vuelven a la cola de Ubicar pallets. No se borra ninguno.',
      boton: 'Vaciar',
    });
    if (!ok) return;
    setTrabajando(true);
    setHecho('');
    setLiberados([]);
    setFallo('');
    try {
      await api.desubicarVarios(elegidos);
      setHecho(`${elegidos.length} ${elegidos.length === 1 ? 'pallet quedó' : 'pallets quedaron'} sin ubicar.`);
      setLiberados(elegidos);
      setMarcados(new Set());
      recargar();
    } catch (err) {
      setFallo(mensajeDeError(err));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>
      <Encabezado bajada="Elegí un rack y marcá qué posiciones liberar. Los pallets quedan sin ubicar." />

      {vistas.includes('racks') && (
        <button type="button" onClick={() => onIr('racks', rackId || undefined)} style={{ ...enlace, fontSize: 13, alignSelf: 'flex-start' }}>
          Volver al mapa
        </button>
      )}

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 360 }}>
        <span style={etiquetaCampo}>Rack</span>
        <select
          value={rackId}
          onChange={(e) => {
            setRackId(e.target.value);
            setMarcados(new Set());
            setHecho('');
            setLiberados([]);
            setFallo('');
          }}
          style={campo}
        >
          <option value="">Elegí un rack…</option>
          {racks.map((r) => (
            <option key={r.id} value={r.id}>
              {r.id} · {r.sector}
            </option>
          ))}
        </select>
      </label>

      <AnimatePresence initial={false}>
      {hecho && (
        <motion.div key="hecho" {...aparecer} role="status" style={aviso('ok')}>
          {hecho}
          {vistas.includes('frac') && (
            <div style={{ marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {liberados.map((p) => (
                <button key={p} type="button" onClick={() => onIr('frac', p)} title="Ver la ficha del pallet" style={{ ...enlace, fontSize: 12.5 }}>
                  {p}
                </button>
              ))}
            </div>
          )}
          {vistas.includes('ubicar') && (
            <div style={{ marginTop: 8 }}>
              <button type="button" onClick={() => onIr('ubicar')} style={{ ...enlace, fontSize: 13 }}>
                Ubicarlos ahora
              </button>
            </div>
          )}
        </motion.div>
      )}
      {fallo && (
        <motion.div key="fallo" {...aparecer} role="alert" style={aviso('err')}>
          {fallo}
        </motion.div>
      )}
      </AnimatePresence>

      {/* La clave es el rack: al cambiar de rack la lista vieja se cierra hacia
          el selector y la nueva crece desde ahí, que es donde se eligió. */}
      <AnimatePresence mode="wait" initial={false}>
      {rack && (
        <motion.div key={rack.id} {...revelarDesde('min(180px, 50%)', '-24px')} style={{ ...tarjeta, overflow: 'hidden', position: 'relative' }}>
          {ocupados.length === 0 ? (
            <Vacio icono="shelves" titulo="El rack está vacío" detalle={`No hay pallets en ${rack.id}.`} />
          ) : (
            <>
              {/* En el celular las cuatro columnas fijas no entran: la fila pasa
                  a dos renglones (ubicación, pallet y piezas arriba; el
                  artículo abajo, alineado con el texto) y los títulos de
                  columna, que ya no tienen debajo qué rotular, se esconden. */}
              <label style={{ ...encabezadoTabla, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={todos}
                  onChange={() => setMarcados(todos ? new Set() : new Set(ocupados.map((o) => o.pallet)))}
                  style={{ width: 20, height: 20 }}
                />
                <span className="tablet:hidden">Todos</span>
                <span className="hidden tablet:inline" style={{ width: 120 }}>Ubicación</span>
                <span className="hidden tablet:inline" style={{ width: 130 }}>Pallet</span>
                <span className="hidden tablet:inline" style={{ flex: 1, minWidth: 0 }}>Artículo</span>
                <span className="hidden tablet:inline" style={{ width: 70, textAlign: 'right' }}>Piezas</span>
              </label>
              {/* Las que se vacían se van y las que quedan suben a ocupar su lugar. */}
              <AnimatePresence mode="popLayout" initial={false}>
              {ocupados.map((o, i) => (
                <motion.label
                  key={o.pallet}
                  layout="position"
                  {...fila(i)}
                  // Marcada se tiñe de verde de a poco; el color va sin el escalón de entrada.
                  animate={{ ...fila(i).animate, backgroundColor: marcados.has(o.pallet) ? c.verdeSuave : `${c.verdeSuave}00` }}
                  transition={{ ...fila(i).transition, backgroundColor: suave, layout: resorte }}
                  className="flex-wrap gap-x-4 gap-y-1 tablet:flex-nowrap tablet:gap-4"
                  style={{ ...filaTabla, gap: undefined, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={marcados.has(o.pallet)}
                    onChange={() => marcar(o.pallet)}
                    style={{ width: 20, height: 20 }}
                  />
                  <span className="tablet:w-[120px]" style={{ fontFamily: mono, fontSize: 12.5 }}>{o.ubicacion}</span>
                  <span className="tablet:w-[130px]" style={{ fontFamily: mono, fontSize: 12.5 }}>{o.pallet}</span>
                  <span className="order-last grow basis-full pl-9 tablet:order-none tablet:basis-0 tablet:pl-0" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {o.articulo ?? '—'}
                  </span>
                  <span className="ml-auto tablet:ml-0 tablet:w-[70px]" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {o.piezas == null ? <span style={{ color: c.texto3 }}>entero</span> : miles(o.piezas)}
                  </span>
                </motion.label>
              ))}
              </AnimatePresence>
              {/* En el celular, a lo ancho: es la acción de la pantalla y suelta a la derecha no se encontraba. */}
              <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => void vaciar()}
                  disabled={trabajando || marcados.size === 0}
                  className="w-full tablet:w-auto"
                  style={{ ...botonPrimario, minHeight: 44, justifyContent: 'center', opacity: trabajando || marcados.size === 0 ? 0.5 : 1 }}
                >
                  Vaciar {marcados.size > 0 && <Numero valor={marcados.size} desdeCero={false} />} {marcados.size === 1 ? 'posición' : 'posiciones'}
                </button>
              </div>
            </>
          )}
        </motion.div>
      )}
      </AnimatePresence>
      {dialogo}
    </div>
  );
}
